import { observable, reaction, runInAction, computed } from 'mobx';
import API from '../util/ApiUtil';
import { urlWithLang } from '../util/NavigationUtil';
import { capitalizeFirstLetter } from '../util/TextUtil';
import LanguageStore from './LanguageStore';
import RootStore from './RootStore';
import RouteStore from './RouteStore';
import ErrorUtil from '../util/ErrorUtil';
import { GravityFormInfo } from '@teliaee/gravity-forms';

export interface PageContent {
  id?: number;
  postId?: number;
  date?: string;
  title?: string;
  originalTitle?: string;
  description?: string;
  link?: string;
  slug?: string;
  image?: string;
  content?: string;
  noindex?: boolean;
  nofollow?: boolean;
  excerpt?: string;
  feedImageUrl?: string;
  hideBreadCrumbs?: boolean;
  whiteBreadCrumbs?: boolean;
  type?: string;
  redirectUrl?: string;
  showFooterCta?: boolean;
  private?: boolean;
}

export const CONTENT_EDIT_LOCAL_STORAGE_KEY = 'contentEditEnabled';
const THIRTY_SECONDS = 30 * 1000;
const DEFAULT_DIIL_IMAGE_URL = 'http://res.cloudinary.com/telia/raw/upload/v1509535214/diil/diil-og.jpg';
const DEFAULT_SUPER_IMAGE_URL = 'https://res.cloudinary.com/telia/image/upload/v1727701275/super/super-OG-1200x630.png';

class ContentStore {
  @observable accessor status: Status = 'UNINITIALIZED';
  @observable accessor currentPageContent: PageContent = {};
  @observable accessor currentContentHeight: number = null;
  @observable accessor formsList: GravityFormInfo[];

  public routeStore: RouteStore;
  private languageStore: LanguageStore;
  private app: App;

  public constructor(rootStore: RootStore) {
    this.routeStore = rootStore.routeStore;
    this.languageStore = rootStore.languageStore;
    this.app = rootStore.app;
    this.formsList = rootStore.initialState.formsList;
    this.init(rootStore.initialState);
    reaction(() => this.routeStore.pathname, this.refreshPath, { fireImmediately: !rootStore.isNode && this.status === 'UNINITIALIZED' });
  }

  private init(initialState: InitialState) {
    if (initialState.initialContent) {
      this.status = 'FETCHED';
      this.currentPageContent = initialState.initialContent;
    }
    this.setupPostEditFunction();
  }

  private setupPostEditFunction = () => {
    if (!window.telia.toggleCMS) {
      window.telia.toggleCMS = () => {
        if (localStorage.getItem(CONTENT_EDIT_LOCAL_STORAGE_KEY)) {
          localStorage.removeItem(CONTENT_EDIT_LOCAL_STORAGE_KEY);
        } else {
          localStorage.setItem(CONTENT_EDIT_LOCAL_STORAGE_KEY, 'true');
        }
        window.location.reload();
      };
    }
  };

  public refreshPath = async (path: string) => {
    if (path !== null && path !== undefined) {
      await this.getPageContentByPath(path);
    }
  };

  private robotsMeta(noindex: boolean, nofollow: boolean) {
    return `${noindex ? 'noindex' : 'index'},${nofollow ? 'nofollow' : 'follow'}`;
  }

  private updateContent(data: PageContent) {
    runInAction(() => {
      this.currentPageContent = data;
      this.status = 'FETCHED';
    });
    this.setTitleAndMetaTags(data);
  }

  public setTitleAndMetaTagsWithCurrentPageContent = () => {
    runInAction(() => this.setTitleAndMetaTags(this.currentPageContent));
  };

  private setTitleAndMetaTags(data: PageContent) {
    const appTitle = capitalizeFirstLetter(this.app);
    const title = data.title ? `${data.title} - ${appTitle}` : appTitle;
    document.querySelector('title').innerText = title;
    document.querySelector('meta[property="og:title"]').setAttribute('content', title);
    document.querySelector('meta[name=description]').setAttribute('content', data.description);
    document.querySelector('meta[name=robots]').setAttribute('content', this.robotsMeta(data.noindex, data.nofollow));
    document.querySelector('meta[property="og:description"]').setAttribute('content', data.description);
    document
      .querySelector('meta[property="og:image"]')
      .setAttribute('content', data.image || (this.app === 'super' ? DEFAULT_SUPER_IMAGE_URL : DEFAULT_DIIL_IMAGE_URL));
  }

  private getPageContentByPath = async (path: string) => {
    runInAction(() => (this.status = 'FETCHING'));
    window.scrollTo(0, 0);
    try {
      const response = await API.get('/content/contentByPath', { params: { path, app: this.app }, timeout: THIRTY_SECONDS });
      const pageContent: PageContent = response.data;
      if (pageContent?.redirectUrl) {
        this.routeStore.replace(urlWithLang(pageContent.redirectUrl, this.languageStore.currentLanguage));
      } else {
        this.updateContent(pageContent);
      }
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      this.resetState('NOT_FOUND');
    }
  };

  public setCurrentContentHeight(height: number) {
    runInAction(() => (this.currentContentHeight = height));
  }

  @computed
  get notFound() {
    return this.status === 'NOT_FOUND';
  }

  @computed
  get hideBreadcrumbs() {
    return this.currentPageContent.hideBreadCrumbs;
  }

  @computed
  get isFetching() {
    return ['UNINITIALIZED', 'FETCHING'].includes(this.status);
  }

  @computed
  get isFetched() {
    return this.status === 'FETCHED';
  }

  private resetState(status: Status) {
    runInAction(() => {
      this.status = status;
      this.currentPageContent = {};
    });
  }
}

export default ContentStore;
